import { useEffect, useMemo, useState, useRef } from 'react';
import Head from 'next/head';
import { Box, Grid } from 'theme-ui';
import { useProductByHandle } from '@backpackjs/storefront';

import { useDataLayerActions } from '@hooks';
import { Header } from './Header';
import { Media } from './Media';
import { Details } from './Details';
import { StickyMobileAddToCart } from './Details/StickyMobileAddToCart';
import { themed } from './Main.theme';

export const Main = themed(
  ({
    theme,
    product,
    selectedVariant,
    productUpsells,
    rebuyProducts,
    videoWise,
  }) => {
    const { product: currentProduct } = useProductByHandle({
      handle: selectedVariant?.product?.handle,
    });
    const { sendViewProductEvent } = useDataLayerActions();

    // Intersection observer for Add to Cart button visibility
    const [isAddToCartVisible, setIsAddToCartVisible] = useState(false);
    const [syncQuantity, setSyncQuantity] = useState(1);

    const titles = useMemo(() => {
      const titleArr = selectedVariant?.product?.title?.split('|');
      return {
        product: titleArr?.[0]?.trim() || '',
        variant: titleArr?.slice(1)?.join('|').trim() || '',
      };
    }, [selectedVariant?.product?.title]);

    useEffect(() => {
      sendViewProductEvent({ product, selectedVariant });
    }, [selectedVariant?.product?.handle, sendViewProductEvent]);

    return (
      <Grid data-comp={Main.displayName} sx={theme.wrapper}>
        <Head>
          <title>{currentProduct?.title || ''}</title>
        </Head>

        <Box sx={{ display: ['block', null, 'none'] }}>
          <Header
            selectedVariant={selectedVariant}
            currentProduct={currentProduct}
            titles={titles}
            isMobile
          />
        </Box>

        {/* Product images */}
        <Media
          product={currentProduct}
          selectedVariant={selectedVariant}
          aboveTheFold
          preload
        />

        {/* Title, Options, Add to cart */}
        <Details
          product={product}
          selectedVariant={selectedVariant}
          currentProduct={currentProduct}
          titles={titles}
          productUpsells={productUpsells}
          rebuyProducts={rebuyProducts}
          videoWise={videoWise}
          setIsAddToCartVisible={setIsAddToCartVisible}
          setSyncQuantity={setSyncQuantity}
          syncQuantity={syncQuantity}
        />

        {/* Sticky Mobile Add to Cart */}
        <StickyMobileAddToCart
          product={currentProduct}
          selectedVariant={selectedVariant}
          rebuyProducts={rebuyProducts}
          isVisible={!isAddToCartVisible}
          syncQuantity={syncQuantity}
          setSyncQuantity={setSyncQuantity}
        />
      </Grid>
    );
  }
);

Main.displayName = 'Main';
